import { useState } from 'react'
import {
  Plus, Trash2, ChevronDown, ChevronUp, X, GitBranch,
  UserPlus, MessageSquare, Mail, RefreshCw, Eye, CheckSquare, Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useOutreachCadences, useCreateCadence, useDeleteCadence } from '@/hooks/useOutreach'
import type { OutreachCadence } from '@/types/outreach'

const ACTION_TYPES = [
  { value: 'send_connection', label: 'Enviar Convite', icon: UserPlus },
  { value: 'send_message', label: 'Enviar Mensagem', icon: MessageSquare },
  { value: 'send_inmail', label: 'Enviar InMail', icon: Mail },
  { value: 'follow_up', label: 'Follow-up', icon: RefreshCw },
  { value: 'view_profile', label: 'Ver Perfil', icon: Eye },
  { value: 'manual_task', label: 'Tarefa Manual', icon: CheckSquare },
]

interface StepForm {
  action_type: string
  delay_days: string
  message_template: string
}

function getActionLabel(type: string) {
  return ACTION_TYPES.find((a) => a.value === type)?.label || type
}

function getActionIcon(type: string) {
  return ACTION_TYPES.find((a) => a.value === type)?.icon || GitBranch
}

export default function CadencesPage() {
  const cadences = useOutreachCadences()
  const createCadence = useCreateCadence()
  const deleteCadence = useDeleteCadence()

  const [showCreate, setShowCreate] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  // Create form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isGlobal, setIsGlobal] = useState(true)
  const [steps, setSteps] = useState<StepForm[]>([
    { action_type: 'send_connection', delay_days: '0', message_template: '' },
  ])

  const resetForm = () => {
    setName('')
    setDescription('')
    setIsGlobal(true)
    setSteps([{ action_type: 'send_connection', delay_days: '0', message_template: '' }])
  }

  const addStep = () => {
    setSteps([...steps, { action_type: 'send_message', delay_days: '3', message_template: '' }])
  }

  const removeStep = (idx: number) => {
    if (steps.length <= 1) return
    setSteps(steps.filter((_, i) => i !== idx))
  }

  const updateStep = (idx: number, field: keyof StepForm, value: string) => {
    const updated = [...steps]
    updated[idx] = { ...updated[idx], [field]: value }
    setSteps(updated)
  }

  const handleCreate = () => {
    if (!name.trim()) return
    createCadence.mutate(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        is_global: isGlobal,
        steps: steps.map((s, i) => ({
          step_order: i + 1,
          action_type: s.action_type,
          delay_days: parseInt(s.delay_days, 10) || 0,
          message_template: s.message_template.trim() || undefined,
        })),
      },
      {
        onSuccess: () => {
          setShowCreate(false)
          resetForm()
        },
      },
    )
  }

  const handleDelete = (id: string) => {
    deleteCadence.mutate(id, {
      onSuccess: () => setDeleteConfirm(null),
    })
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cadencias</h1>
          <p className="text-muted-foreground mt-1">Sequencias automatizadas de outreach</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nova Cadencia
        </button>
      </div>

      {/* Cadences List */}
      {cadences.isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : cadences.data?.items?.length ? (
        <div className="space-y-3">
          {cadences.data.items.map((cadence) => {
            const expanded = expandedId === cadence.id
            return (
              <div key={cadence.id} className="bg-card border rounded-xl overflow-hidden">
                {/* Cadence header */}
                <button
                  onClick={() => setExpandedId(expanded ? null : cadence.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-accent/30 transition-colors text-left"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{cadence.name}</span>
                      {cadence.is_global && (
                        <span className="text-[10px] font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          Global
                        </span>
                      )}
                    </div>
                    {cadence.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{cadence.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {cadence.total_steps} passo{cadence.total_steps !== 1 ? 's' : ''}
                    </p>
                  </div>
                  {expanded ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>

                {/* Expanded steps */}
                {expanded && (
                  <div className="border-t px-4 py-3 space-y-3">
                    {(cadence.steps || [])
                      .sort((a, b) => a.step_order - b.step_order)
                      .map((step, idx) => {
                        const ActionIcon = getActionIcon(step.action_type)
                        return (
                          <div key={step.id} className="flex items-start gap-3">
                            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-[11px] font-bold text-primary-foreground">{idx + 1}</span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <ActionIcon className="w-3.5 h-3.5 text-primary" />
                                <span className="text-sm font-medium">{getActionLabel(step.action_type)}</span>
                                <span className="text-xs text-muted-foreground">
                                  {step.delay_days === 0 ? 'Imediato' : `+${step.delay_days}d`}
                                </span>
                              </div>
                              {step.message_template && (
                                <p className="text-xs text-muted-foreground mt-1 italic line-clamp-2">
                                  {step.message_template}
                                </p>
                              )}
                            </div>
                          </div>
                        )
                      })}

                    {/* Delete button */}
                    {deleteConfirm === cadence.id ? (
                      <div className="flex items-center gap-2 pt-2 border-t">
                        <span className="text-xs text-destructive">Confirmar exclusao?</span>
                        <button
                          onClick={() => handleDelete(cadence.id)}
                          className="text-xs font-semibold text-destructive hover:underline"
                        >
                          Sim, excluir
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-xs text-muted-foreground hover:underline"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(cadence.id)}
                        className="flex items-center gap-1.5 text-xs font-medium text-destructive hover:underline pt-2 border-t"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Excluir cadencia
                      </button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div className="bg-card border rounded-xl p-12 text-center">
          <GitBranch className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold">Nenhuma cadencia criada</h3>
          <p className="text-sm text-muted-foreground mt-1">Crie uma cadencia para automatizar seu outreach.</p>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => { setShowCreate(false); resetForm() }} />
          <div className="relative bg-background border rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Nova Cadencia</h2>
              <button onClick={() => { setShowCreate(false); resetForm() }} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Name */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Nome *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Prospeccao Enterprise"
                className="mt-1 flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Descricao</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descricao da cadencia"
                className="mt-1 flex h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
              />
            </div>

            {/* Global toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isGlobal}
                onChange={(e) => setIsGlobal(e.target.checked)}
                className="w-4 h-4 rounded border-input text-primary"
              />
              <span className="text-sm">Disponivel para todo o time</span>
            </label>

            {/* Steps */}
            <div>
              <h3 className="font-semibold mb-2">Passos da Cadencia</h3>
              <div className="space-y-3">
                {steps.map((step, idx) => (
                  <div key={idx} className="bg-card border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-primary">Passo {idx + 1}</span>
                      {steps.length > 1 && (
                        <button onClick={() => removeStep(idx)} className="text-destructive hover:text-destructive/80">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Action type chips */}
                    <div>
                      <label className="text-[11px] font-medium text-muted-foreground">Acao</label>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {ACTION_TYPES.map((at) => {
                          const AtIcon = at.icon
                          const selected = step.action_type === at.value
                          return (
                            <button
                              key={at.value}
                              onClick={() => updateStep(idx, 'action_type', at.value)}
                              className={cn(
                                'flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-[11px] font-medium transition-colors',
                                selected
                                  ? 'bg-primary/10 border-primary text-primary'
                                  : 'border-input text-muted-foreground hover:bg-accent'
                              )}
                            >
                              <AtIcon className="w-3 h-3" />
                              {at.label}
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    {/* Delay */}
                    <div>
                      <label className="text-[11px] font-medium text-muted-foreground">Atraso (dias)</label>
                      <input
                        type="number"
                        min="0"
                        value={step.delay_days}
                        onChange={(e) => updateStep(idx, 'delay_days', e.target.value.replace(/[^0-9]/g, ''))}
                        className="mt-1 flex h-9 w-20 rounded-md border border-input bg-background px-3 text-sm"
                      />
                    </div>

                    {/* Message template */}
                    <div>
                      <label className="text-[11px] font-medium text-muted-foreground">Modelo de Mensagem</label>
                      <textarea
                        value={step.message_template}
                        onChange={(e) => updateStep(idx, 'message_template', e.target.value)}
                        placeholder="Ola {name}, notei que..."
                        rows={2}
                        className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={addStep}
                className="mt-2 flex items-center justify-center gap-1.5 w-full py-2.5 border border-dashed border-primary/40 rounded-lg text-xs font-medium text-primary hover:bg-primary/5 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Adicionar Passo
              </button>
            </div>

            {/* Submit */}
            <button
              onClick={handleCreate}
              disabled={createCadence.isPending || !name.trim()}
              className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {createCadence.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Criar Cadencia'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
